import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  ClipboardCheck,
  Users,
  FileText,
  Receipt,
  FileBarChart,
  Wrench,
  FileUp,
  CalendarDays,
  Inbox,
  Shield,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ArrowLeftRight,
  Sparkles,
} from "lucide-react";
import { useAuth, canSwitchMode } from "@/context/AuthContext";
import { PlanProvider, usePlan } from "@/context/PlanContext";
import UpgradeDialog from "@/components/UpgradeDialog";
import LegalGate from "@/components/LegalGate";
import LegalFooter from "@/components/LegalFooter";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
  { to: "/buildings", label: "Buildings", icon: Building2, testId: "nav-buildings" },
  { to: "/clients", label: "Clients", icon: Users, testId: "nav-clients" },
  { to: "/inspections", label: "Inspections", icon: ClipboardCheck, testId: "nav-inspections" },
  { to: "/schedule", label: "Schedule", icon: CalendarDays, testId: "nav-schedule" },
  { to: "/quotes", label: "Quotes", icon: FileText, testId: "nav-quotes" },
  { to: "/maintenance", label: "Maintenance", icon: Wrench, testId: "nav-maintenance" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testId: "nav-invoices" },
  { to: "/inspection-requests", label: "Requests", icon: Inbox, testId: "nav-requests" },
  { to: "/imports", label: "Imports", icon: FileUp, testId: "nav-imports" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testId: "nav-reports" },
];

function initials(name) {
  return String(name || "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// Topbar free-trial countdown → nudges conversion and opens the plans page.
function TrialPill() {
  const { isTrial, daysLeft, loading } = usePlan();
  const navigate = useNavigate();
  if (loading || !isTrial) return null;
  const n = typeof daysLeft === "number" ? daysLeft : null;
  return (
    <button
      onClick={() => navigate("/settings?tab=subscription")}
      data-testid="trial-pill"
      className="mr-1 hidden items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100 sm:flex"
    >
      <Sparkles className="h-3.5 w-3.5" />
      {n === null ? "Free trial" : n <= 0 ? "Trial ended" : `Free trial · ${n}d left`}
      <span className="font-bold underline">Upgrade</span>
    </button>
  );
}

function SidebarContent({ user, onNavigate }) {
  const items = [...NAV];
  if (user?.is_company_admin || user?.is_platform_admin) {
    items.push({ to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" });
  }
  if (user?.is_platform_admin) {
    items.push({ to: "/platform", label: "Super Admin", icon: Shield, testId: "nav-platform" });
  }
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-100">
        <img src="/fdcheck-logo.jpg" alt="FD Check" className="h-10 w-auto object-contain" />
      </div>
      <nav className="flex-1 space-y-1 px-3 py-4">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={item.testId}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                )
              }
            >
              <Icon className="h-[18px] w-[18px]" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="px-5 py-4 border-t border-slate-100">
        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
          {user?.company?.name || "FD Check"}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">Fire door compliance</p>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, logout, impersonating, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <PlanProvider>
    <div className="min-h-screen bg-slate-50">
      <UpgradeDialog />
      <LegalGate />
      {impersonating && (
        <div className="fixed inset-x-0 top-0 z-50 flex items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-sm font-semibold text-amber-950" data-testid="impersonation-banner">
          <span>Impersonating {user?.name || impersonating.label} — viewing another tenant on their behalf.</span>
          <button onClick={exitImpersonation} className="rounded-full bg-amber-950/10 px-3 py-0.5 text-xs font-bold hover:bg-amber-950/20" data-testid="exit-impersonation-btn">
            Exit
          </button>
        </div>
      )}
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 z-30 w-64 flex-col border-r border-slate-200 bg-white">
        <SidebarContent user={user} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-nav-backdrop"
          />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white shadow-xl">
            <button
              className="absolute right-3 top-4 text-slate-400"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent user={user} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-4 backdrop-blur lg:px-8">
          <button
            className="lg:hidden text-slate-600"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-nav-toggle"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1" />
          <TrialPill />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-trigger">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {initials(user?.name)}
                </span>
                <span className="hidden text-left sm:block">
                  <span className="block text-sm font-semibold leading-tight text-slate-800">
                    {user?.name}
                  </span>
                  <span className="block text-xs capitalize leading-tight text-slate-500">
                    {user?.is_platform_admin ? "Super Admin" : user?.is_company_admin ? "Admin" : user?.role}
                  </span>
                </span>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-semibold">{user?.name}</div>
                <div className="text-xs font-normal text-slate-500">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {canSwitchMode(user) && (
                <>
                  <DropdownMenuItem onClick={() => navigate("/mode")} data-testid="switch-mode-btn">
                    <ArrowLeftRight className="mr-2 h-4 w-4" /> Switch mode
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-600 focus:text-red-600">
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="px-4 py-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
        <LegalFooter className="pb-8 pt-2" />
      </div>
    </div>
    </PlanProvider>
  );
}
